// AuthContext (apps/admin adapter) — useAuth() backed by apps/admin's Redux auth.
//
// The relocated DashAI admin panel calls useAuth() expecting { firestoreUser, loading }.
// In apps/web this is provided by DashAIAuthAdapter bridging DashONE's Redux state.
// apps/admin has its own Redux auth slice (state.auth), so this adapter sources the
// data from there instead. The TYPE definitions are copied verbatim from apps/web's
// AuthContext so the relocated components' type imports resolve unchanged.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// ---------- Type definitions (verbatim from apps/web AuthContext) ----------

export interface GeoscopeRegion {
  key: string;
  name: string;
  markets: string[];
}

export interface CoreBrandContent {
  brand: string;
  subbrand: string | null;
}

export interface CoreBrand {
  name: string;
  content: CoreBrandContent[];
}

export interface Competitor {
  company: string;
  brand: string | null;
}

export interface Weights {
  combo_presence?: number;
  core_availability?: number;
  fixed_combo_presence?: number;
  image_presence?: number;
  market_penetration?: number;
  market_penetration_branded?: number;
  name_accuracy?: number;
  option_combo_presence?: number;
  [key: string]: number | undefined;
}

export interface MarketFormula {
  id_version: string;
  companies: string[];
  core_brands: CoreBrand[];
  competitors: Competitor[];
  weights: Weights;
}

export interface PetFormula {
  version_name: string;
  is_active: boolean;
  formula: Record<string, MarketFormula>;
}

export interface ClientPermission {
  is_super_admin: boolean;
  product_scopes: Record<string, string[]> | string[] | null;
  role: string;
  accessible_countries: string[];
  geoscope?: {
    regions: GeoscopeRegion[];
  };
  pet_formula?: PetFormula | null;
}

export interface FirestoreUser {
  userId: string;
  email: string;
  name: string;
  application_role: 'user' | 'admin' | 'viewer';
  workspaces: string[];
  default_id_client: string;
  id_client: Record<string, ClientPermission>;
  // User-level Dashmote-admin flag (the /admin gate), not a per-workspace role.
  dashmote_admin?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ---------- Adapter ----------

interface AuthContextValue {
  firestoreUser: FirestoreUser | null;
  loading: boolean;
}

// Builds the FirestoreUser shape the relocated admin panel expects from
// apps/admin's Redux auth slice. Returns firestoreUser: null when the user
// is not authenticated or userData hasn't loaded yet.
export function useAuth(): AuthContextValue {
  const reduxUser = useSelector((state: any) => state.auth.user);
  const userData = useSelector((state: any) => state.auth.userData);
  const loading = useSelector((state: any) => state.auth.loading);

  const firestoreUser: FirestoreUser | null = useMemo(() => {
    if (!reduxUser || !userData) return null;

    const idClient = (userData.id_client && typeof userData.id_client === 'object'
      ? userData.id_client
      : {}) as Record<string, ClientPermission>;

    return {
      userId: reduxUser.uid,
      email: reduxUser.email || '',
      name: reduxUser.displayName || userData.name || '',
      application_role: userData.application_role || 'viewer',
      workspaces: Object.keys(idClient),
      default_id_client: userData.default_id_client || '',
      id_client: idClient,
      dashmote_admin: Boolean(userData.dashmote_admin),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }, [reduxUser, userData]);

  return { firestoreUser, loading: Boolean(loading) };
}
